/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import React from 'react';
import TeamLogo from 'ud-ui/components/team-logo';
import UDText from 'ud-ui/components/text';
import { AnalyticsMatchRecord, AnalyticsMatchTeam } from 'modules/analytics/domain/interfaces/tournament-analytics';
import { AnalyticsTone } from './icon-badge';
import RecordCardShell from './record-card-shell';

interface MatchRecordCardProps {
  title: string;
  icon: React.ReactNode;
  record: AnalyticsMatchRecord | null;
  highlightLabel: string;
  tone?: AnalyticsTone;
  className?: string;
}

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const TeamSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  text-align: center;
`;

/** Табло счёта: утопленная плашка, чтобы цифры читались как результат, а не как подпись. */
const Scoreboard = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 18px;
  border-radius: ${({ theme }) => theme.radii.card};
  background: ${({ theme }) => theme.colors.SurfaceSunken};
`;

const TeamBlock = ({ team, isWinner }: { team: AnalyticsMatchTeam; isWinner: boolean }) => (
  <TeamSide>
    <TeamLogo url={team.emblemUrl ?? undefined} name={team.name} size={56} />
    <UDText variant={isWinner ? 'title-4-bold' : 'title-4'} as="div" className={isWinner ? '!text-inkPrimary' : '!text-inkSecondary'}>
      {team.name}
    </UDText>
  </TeamSide>
);

const MatchRecordCard = React.memo(({ title, icon, record, highlightLabel, tone = 'gold', className }: MatchRecordCardProps) => {
  if (record === null) {
    return <RecordCardShell icon={icon} title={title} tone={tone} isEmpty className={className} />;
  }

  const { team1, team2 } = record;

  return (
    <RecordCardShell
      icon={icon}
      title={title}
      tone={tone}
      highlight={`${highlightLabel}: ${record.highlightValue}`}
      championshipName={record.championshipName}
      matchDate={record.matchDate}
      className={className}
    >
      <ScoreRow>
        <TeamBlock team={team1} isWinner={team1.score > team2.score} />
        <Scoreboard>
          <UDText variant="title-1" as="span" className="!text-inkPrimary leading-none">
            {team1.score}
          </UDText>
          <UDText variant="title-3" as="span" className="!text-inkMuted leading-none">
            :
          </UDText>
          <UDText variant="title-1" as="span" className="!text-inkPrimary leading-none">
            {team2.score}
          </UDText>
        </Scoreboard>
        <TeamBlock team={team2} isWinner={team2.score > team1.score} />
      </ScoreRow>
    </RecordCardShell>
  );
});

export default MatchRecordCard;
